import {isLoaded as isLoadedStatus, Status, UnassignedStatus} from 'ApplicationEntry/models/AWDStatus/Status';
import {UnassignedUnderwriter, Underwriter} from 'ApplicationEntry/models/AWDStatus/Underwriter';
import {isLoaded as isLoadedWorkType, UnassignedWorkType, WorkType} from 'ApplicationEntry/models/AWDStatus/WorkType';
import {Loaded} from 'Core/models/Async';
import {EmptyString, isNonEmptyString, NonEmptyString, StringUnion} from 'Core/models/EmptyString';
import {ToPartialMatchOptions} from 'Shared/utilities/MapDiscriminatedUnion';
import {UpdateWork} from 'ApplicationEntry/models/AWDStatus/UpdateWork';

type AWDStatusProps = {
  workType: StringUnion;
  workType2: StringUnion;
  workType3: StringUnion;
  status: StringUnion;
  status2: StringUnion;
  status3: StringUnion;
  underwriter: StringUnion;
  underwriter2: StringUnion;
  underwriter3: StringUnion;
  comments: StringUnion;
  comments2: StringUnion;
  comments3: StringUnion;
  override: boolean;
  override2: boolean;
  override3: boolean;
  workTypeList: WorkType;
  statusList: Status;
  underwriterList: Underwriter;
};
export class InvalidNewYorkAWDStatus {
  public readonly key = 'InvalidNewYorkAWDStatus';

  constructor(
    public readonly workType: StringUnion,
    public readonly workType2: StringUnion,
    public readonly workType3: StringUnion,
    public readonly status: StringUnion,
    public readonly status2: StringUnion,
    public readonly status3: StringUnion,
    public readonly underwriter: StringUnion,
    public readonly underwriter2: StringUnion,
    public readonly underwriter3: StringUnion,
    public readonly comments: StringUnion,
    public readonly comments2: StringUnion,
    public readonly comments3: StringUnion,
    public readonly override: boolean,
    public readonly override2: boolean,
    public readonly override3: boolean,
    public readonly workTypeList: WorkType,
    public readonly statusList: Status,
    public readonly underwriterList: Underwriter
  ) {}
}

export class ValidNewYorkAWDStatus {
  public static createApp({
    workType,
    workType2,
    workType3,
    status,
    status2,
    status3,
    underwriter,
    underwriter2,
    underwriter3,
    comments,
    comments2,
    comments3,
    override,
    override2,
    override3,
    workTypeList,
    statusList,
    underwriterList,
  }: AWDStatusProps) {
    return this.create(
      workType,
      workType2,
      workType3,
      status,
      status2,
      status3,
      underwriter,
      underwriter2,
      underwriter3,
      comments,
      comments2,
      comments3,
      override,
      override2,
      override3,
      workTypeList,
      statusList,
      underwriterList
    );
  }

  public static create(
    workType: StringUnion,
    workType2: StringUnion,
    workType3: StringUnion,
    status: StringUnion,
    status2: StringUnion,
    status3: StringUnion,
    underwriter: StringUnion,
    underwriter2: StringUnion,
    underwriter3: StringUnion,
    comments: StringUnion,
    comments2: StringUnion,
    comments3: StringUnion,
    override: boolean,
    override2: boolean,
    override3: boolean,
    workTypeList: WorkType,
    statusList: Status,
    underwriterList: Underwriter
  ) {
    return isLoadedWorkType(workTypeList) &&
      isNonEmptyString(workType) &&
      isLoadedStatus(statusList) &&
      isNonEmptyString(status)
      ? new ValidNewYorkAWDStatus(
          workType,
          workType2,
          workType3,
          status,
          status2,
          status3,
          underwriter,
          underwriter2,
          underwriter3,
          comments,
          comments2,
          comments3,
          override,
          override2,
          override3,
          workTypeList,
          statusList,
          underwriterList
        )
      : new InvalidNewYorkAWDStatus(
          workType,
          workType2,
          workType3,
          status,
          status2,
          status3,
          underwriter,
          underwriter2,
          underwriter3,
          comments,
          comments2,
          comments3,
          override,
          override2,
          override3,
          workTypeList,
          statusList,
          underwriterList
        );
  }

  public readonly key = 'ValidNewYorkAWDStatus';
  private constructor(
    public readonly workType: StringUnion,
    public readonly workType2: StringUnion,
    public readonly workType3: StringUnion,
    public readonly status: StringUnion,
    public readonly status2: StringUnion,
    public readonly status3: StringUnion,
    public readonly underwriter: StringUnion,
    public readonly underwriter2: StringUnion,
    public readonly underwriter3: StringUnion,
    public readonly comments: StringUnion,
    public readonly comments2: StringUnion,
    public readonly comments3: StringUnion,
    public readonly override: boolean,
    public readonly override2: boolean,
    public readonly override3: boolean,
    public readonly workTypeList: Loaded<string[]>,
    public readonly statusList: Loaded<string[]>,
    public readonly underwriterList: Underwriter
  ) {}
}

export type NewYorkAWDStatus = ValidNewYorkAWDStatus | InvalidNewYorkAWDStatus;

export type NewYorkAWDStatusOptions<TReturn> = ToPartialMatchOptions<NewYorkAWDStatus, 'key', TReturn>;

export const matchNewYorkAWDStatus = <TReturn>(
  awdStatus: NewYorkAWDStatus,
  options: NewYorkAWDStatusOptions<TReturn>
): TReturn => {
  switch (awdStatus.key) {
    case 'InvalidNewYorkAWDStatus':
      return options.InvalidNewYorkAWDStatus ? options.InvalidNewYorkAWDStatus(awdStatus) : options.default(awdStatus);
    case 'ValidNewYorkAWDStatus':
      return options.ValidNewYorkAWDStatus ? options.ValidNewYorkAWDStatus(awdStatus) : options.default(awdStatus);
  }
};

export const changeWorkType = (workType: string) => (app: NewYorkAWDStatus) =>
  ValidNewYorkAWDStatus.createApp({...app, workType: NonEmptyString.create(workType)});
export const changeWorkType2 = (workType2: string) => (app: NewYorkAWDStatus) =>
  ValidNewYorkAWDStatus.createApp({...app, workType2: NonEmptyString.create(workType2)});
export const changeWorkType3 = (workType3: string) => (app: NewYorkAWDStatus) =>
  ValidNewYorkAWDStatus.createApp({...app, workType3: NonEmptyString.create(workType3)});
export const changeStatus = (status: string) => (app: NewYorkAWDStatus) =>
  ValidNewYorkAWDStatus.createApp({...app, status: NonEmptyString.create(status)});
export const changeStatus2 = (status2: string) => (app: NewYorkAWDStatus) =>
  ValidNewYorkAWDStatus.createApp({...app, status2: NonEmptyString.create(status2)});
export const changeStatus3 = (status3: string) => (app: NewYorkAWDStatus) =>
  ValidNewYorkAWDStatus.createApp({...app, status3: NonEmptyString.create(status3)});
export const changeUnderwriter = (underwriter: string) => (app: NewYorkAWDStatus) =>
  ValidNewYorkAWDStatus.createApp({...app, underwriter: NonEmptyString.create(underwriter)});
export const changeUnderwriter2 = (underwriter2: string) => (app: NewYorkAWDStatus) =>
  ValidNewYorkAWDStatus.createApp({...app, underwriter2: NonEmptyString.create(underwriter2)});
export const changeUnderwriter3 = (underwriter3: string) => (app: NewYorkAWDStatus) =>
  ValidNewYorkAWDStatus.createApp({...app, underwriter3: NonEmptyString.create(underwriter3)});
export const changeComments = (comments: string) => (app: NewYorkAWDStatus) => {
  return ValidNewYorkAWDStatus.createApp({...app, comments: NonEmptyString.create(comments)});
};
export const changeComments2 = (comments2: string) => (app: NewYorkAWDStatus) => {
  return ValidNewYorkAWDStatus.createApp({...app, comments2: NonEmptyString.create(comments2)});
};
export const changeComments3 = (comments3: string) => (app: NewYorkAWDStatus) => {
  return ValidNewYorkAWDStatus.createApp({...app, comments3: NonEmptyString.create(comments3)});
};
export const changeOverride = () => (app: NewYorkAWDStatus) =>
  ValidNewYorkAWDStatus.createApp({...app, override: !app.override});
export const changeOverride2 = () => (app: NewYorkAWDStatus) =>
  ValidNewYorkAWDStatus.createApp({...app, override2: !app.override2});
export const changeOverride3 = () => (app: NewYorkAWDStatus) =>
  ValidNewYorkAWDStatus.createApp({...app, override3: !app.override3});
export const changeWorkTypeList = (workTypeList: WorkType) => (app: NewYorkAWDStatus) =>
  ValidNewYorkAWDStatus.createApp({...app, workTypeList});
export const changeStatusList = (statusList: Status) => (app: NewYorkAWDStatus) =>
  ValidNewYorkAWDStatus.createApp({...app, statusList});
export const changeUnderwriterList = (underwriterList: Underwriter) => (app: NewYorkAWDStatus) =>
  ValidNewYorkAWDStatus.createApp({...app, underwriterList});
export const resetApp = () => (app: NewYorkAWDStatus) =>
  ValidNewYorkAWDStatus.createApp({
    ...app,
    workTypeList: new UnassignedWorkType(),
    statusList: new UnassignedStatus(),
    underwriterList: new UnassignedUnderwriter(),
    workType: new EmptyString(),
    status: new EmptyString(),
    underwriter: new EmptyString(),
    override: false,
    comments: new EmptyString(),
  });

export const EmptyNewYorkAWDStatus = new InvalidNewYorkAWDStatus(
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  false,
  false,
  false,
  new UnassignedWorkType(),
  new UnassignedStatus(),
  new UnassignedUnderwriter()
);
